import React, { useState, useEffect } from 'react'
import { Box, Divider, TextField, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { extractPdf, extractPdfV2 } from 'services'
import { useServices } from 'contexts'
import { errorStateTexts } from 'components/CreateInterview/constants'
import FileUpload from 'components/FileUpload'
import LinearProgressBar from 'components/LinearProgressBar'
import FileUploadErrorDialog from 'dialogs/FileUploadErrorDialog'
import { IconButton } from './CustomButton'
import { Check, Clear } from '@mui/icons-material'
import { useTheme } from '@emotion/react'

const useStyles = makeStyles((theme) => ({
    uploadContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2)
    },
    uploadedContainer: {
      display: 'flex',
      gap: theme.spacing(4.5),
      padding: theme.spacing(3, 2),
      border: `1px solid ${theme.palette.neutral['clr-300']}`,
      borderRadius: theme.spacing(1),
    },
    head: {
      display: 'flex',
      flexDirection: 'row',
      gap:theme.spacing(2)
    },
    iconsBox: {
      display: 'flex',
      gap : theme.spacing(3),
    },
    editBox: {
      display: 'flex',
      gap: theme.spacing(2)
    },
    deleteImage: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
}))

export const FileUploadSection = ({ 
  fileType, 
  title='', 
  iconSrc,
  uploadName,
  changeText,
  onFileProcessed,
  width = '318px',
  showDate = false,
  uploadToS3 = false
}) => {
  const classes = useStyles();
  const service = useServices();
  const theme = useTheme();

  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [fileNameReceived, setFileNameReceived] = useState(false);
  const [progressBar, setProgressBar] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [fileName, setFileName] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [editingResume, setEditingResume] = useState(null);

  useEffect(() => {
    if (fileNameReceived) {
      setError(false);
      setProgressBar(true);
    }
  }, [fileNameReceived]);

  const pdfToTextScrape = async (file) => {
    try {
      if(uploadToS3){
        const result = await service.callService(extractPdfV2, file);
        return result;
      }else{
        const result = await service.callService(extractPdf, file);
        return result;
      }
    } catch (error) {
      console.error("Error extracting text from PDF:", error);
      throw error;
    }
  };

  const extractPdfData = async (file) => {
    try {
      const extracted = await pdfToTextScrape(file);
      const extractedText = uploadToS3 ? extracted?.extractedText : extracted;
  
      const isEmpty = !extractedText || extractedText.trim().length === 0;
  
      if (isEmpty) {
        setError(true);
        setErrorText(errorStateTexts.scanned_document);
        return null;
      }
  
      return uploadToS3 ? extracted : extractedText;
  
    } catch (err) {
      console.error("Failed to extract text from PDF:", err);
      setError(true);
      setErrorText(errorStateTexts.pdf_upload_fail);
      return null;
    }
  };
  

  const handleFileSelect = async (selectedFile) => {
    if (selectedFile.type !== "application/pdf") {
      setError(true);
      setErrorText(errorStateTexts.not_pdf_file);
      return false;
    }

    const extractedData = await extractPdfData(selectedFile);

    if (extractedData) {
      let fileNameWithoutExtension = selectedFile.name.replace(/\.pdf$/i, '');
      
      setFileName(fileNameWithoutExtension);
      setFileNameReceived(true);
      setIsFileUploaded(true);
      
      if (onFileProcessed) {
        onFileProcessed({
          fileName: fileNameWithoutExtension,
          scrapedData: extractedData,
          fileType
        });
      }
      
      return true;
    }
    return false;
  };

  // Component for file upload UI
  const getUploadComponent = () => {
    if (progressBar && fileNameReceived) {
      return (
        <LinearProgressBar
          fileNameReceived={fileNameReceived}
          setFileNameReceived={setFileNameReceived}
          isFileUploaded={isFileUploaded}
          fileName={fileName}
          error={error}
          setProgressBar={setProgressBar}
          isCaseStudy={false}
          setRefresh={setRefresh}
        />
      );
    } else if (refresh) {
      return (
        <Box className={classes.uploadedContainer} sx={{width : width}}>
          <img
            src={iconSrc}
            alt={fileType}
            width={44}
          />
          <Box display="flex" flexDirection="column" gap="4px" width='100%'>
           
                              <Typography
                              variant="body01-semiBold"
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "200px",
                              }}
                            >
                              {fileName}
                            </Typography>
                            <Divider sx={{width : '100%'}}/>
            {showDate ? (
              <Box display='flex' gap='4px'>
                <Typography variant='body01-medium'>Added on:</Typography>
                <Typography variant='body01-semiBold'>17/06/2024</Typography>
              </Box>
              ) 
            : (
               <FileUpload
                  name={uploadName}
                  label="Upload"
                  onFileSelect={handleFileSelect}
                  uploadOnAws={false}
                  accept={[".pdf"]}
                  variant="outlined"
                  buttonSize="medium"
                  buttonType={
                    <Typography
                      variant="body01-link"
                      color="primary.clr-300"
                      sx={{ cursor: "pointer" }}
                    >
                      Change {changeText}
                    </Typography>
                  }
                />
              )}             
            </Box>
          </Box>
      );
    } else {
      return (
        <FileUpload
          name={uploadName}
          label="Upload"
          onFileSelect={handleFileSelect}
          uploadOnAws={false}
          accept={[".pdf"]}
          variant="outlined"
          buttonSize="medium"
          sx={{ width: width }}
        />
      );
    }
  };

  return (
    <>
      <Box className={classes.uploadContainer}>
        <Typography variant='body01-bold' color='neutral.clr-900'>{title}</Typography>
        {getUploadComponent()}
      </Box>
      
      {/* Error Dialog */}
      <FileUploadErrorDialog
        open={error}
        fileUploadProp={
          <FileUpload 
            name={uploadName}
            label="Upload"
            onFileSelect={handleFileSelect}
            uploadOnAws={false}
            accept={[".pdf"]}
            variant='contained'
            buttonSize='medium'
            sx={{width:'100%'}}
          />
        }
        title={<Box color='danger.clr-700'>File upload failed!</Box>}
        message={
          <Box>
            <Typography variant='h6-regular' color='nuetral.clr-900'>
              {errorText}
            </Typography>
          </Box>
        }
        buttonSize='medium'
        setProgressBar={setProgressBar}
        fileNameReceived={fileNameReceived}
        isCaseStudy={true}
        onSecondaryAction={() => {
          setError(false);
        }}
      />
    </>
  );
};

FileUploadSection.defaultProps = {
  uploadName: "file",
  changeText: "file"
};
